import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  normalizeCallMode,
  requiredMediaForMode,
  saveDeviceReadiness,
} from "../../util/deviceReadiness";
// Recording-indicator fix: this component's own getUserMedia() preview
// stream was only ever tracked in a local ref, cleaned up solely by this
// component's own unmount effect. If that unmount is ever skipped (e.g. a
// hard redirect elsewhere in the call-abort flow instead of a normal SPA
// route change), nothing else in the app knew this stream existed, so the
// browser tab's mic/camera indicator could stay lit indefinitely. Registering
// it here means the shared stopAllRegisteredMedia() safety net — already
// called on every call-end path and on Appointments list mount — will also
// catch and stop it even if this component's own cleanup did not run.
import { registerMediaStream } from "../../util/mediaTrackRegistry";

function stopStream(stream) {
  try {
    stream?.getTracks?.().forEach((track) => track.stop());
  } catch (_) {
    /* ignore */
  }
}

function friendlyDeviceLabel(device, index, kind) {
  let raw = String(device?.label || "").trim();
  const fallback =
    kind === "audiooutput"
      ? `Speaker ${index + 1}`
      : kind === "audioinput"
        ? `Microphone ${index + 1}`
        : `Camera ${index + 1}`;
  if (!raw) return fallback;

  // Clean technical names and brand marks
  raw = raw.replace(/\(R\)|\(TM\)/gi, "");
  raw = raw.replace(/\([0-9a-fA-F]{4}:[0-9a-fA-F]{4}\)/g, ""); // clean (hex:hex) tech details
  raw = raw.replace(/\s+/g, " ").trim();

  const lower = raw.toLowerCase();
  const rest = raw.replace(/^(default|communications)\s*[-–:]?\s*/i, "").trim();
  if (lower.startsWith("default")) return rest ? `Default — ${rest}` : "Default device";
  if (lower.startsWith("communications")) return rest ? `Communications — ${rest}` : "Communications device";
  return raw;
}

async function playSpeakerTest(deviceId) {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return false;
  const audio = new Audio();
  try {
    if (deviceId && typeof audio.setSinkId === "function") {
      await audio.setSinkId(deviceId);
    }
  } catch (_) {
    /* setSinkId is optional */
  }
  const ctx = new AudioContext();
  try {
    if (ctx.state === "suspended") await ctx.resume();

    // Play a dual-tone pleasant synth chime instead of a single flat beep
    const playChime = async (freq, startTime, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(0.08, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };

    const now = ctx.currentTime;
    await playChime(523.25, now, 0.3); // C5
    await playChime(659.25, now + 0.15, 0.3); // E5
    await playChime(783.99, now + 0.3, 0.4); // G5

    await new Promise((resolve) => setTimeout(resolve, 800));
    return true;
  } catch (_) {
    return false;
  } finally {
    try {
      await ctx.close();
    } catch (_) {
      /* ignore */
    }
  }
}

function mediaErrorMessage(error, needsVideo) {
  const name = error?.name || "";
  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    return needsVideo
      ? "Camera and microphone access is blocked. Please allow both in your browser's site settings to proceed."
      : "Microphone access is blocked. Please allow it in your browser's site settings to proceed.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return needsVideo
      ? "A working camera and microphone are required for this video consultation."
      : "A working microphone is required for this voice consultation.";
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return "The required device is busy or unavailable. Close other camera or microphone apps, then retry.";
  }
  return "We could not start the required devices. Check your browser permissions and try again.";
}

export default function PreCallDeviceCheck({
  open = false,
  mode = "video",
  consultantReadiness = false,
  onReady,
  onCancel,
}) {
  const normalizedMode = normalizeCallMode(mode);
  const requirements = requiredMediaForMode(normalizedMode);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cameraIdRef = useRef("");
  const microphoneIdRef = useRef("");
  const audioContextRef = useRef(null);
  const animationRef = useRef(null);
  const mountedRef = useRef(true);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [errorName, setErrorName] = useState("");
  const [devices, setDevices] = useState({ cameras: [], microphones: [], speakers: [] });
  const [cameraId, setCameraId] = useState("");
  const [microphoneId, setMicrophoneId] = useState("");
  const [speakerId, setSpeakerId] = useState("");
  const [speakerTested, setSpeakerTested] = useState(false);
  const [isPlayingSpeakerTest, setIsPlayingSpeakerTest] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [cameraFailed, setCameraFailed] = useState(false);

  const releaseDevices = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => { });
      audioContextRef.current = null;
    }
    stopStream(streamRef.current);
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    if (mountedRef.current) setMicLevel(0);
  }, []);

  const startMeter = useCallback((stream) => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      const source = context.createMediaStreamSource(stream);
      source.connect(analyser);
      const values = new Uint8Array(analyser.frequencyBinCount);
      audioContextRef.current = context;
      const tick = () => {
        analyser.getByteFrequencyData(values);
        const average = values.reduce((sum, value) => sum + value, 0) / values.length;
        if (mountedRef.current) setMicLevel(Math.min(100, Math.round(average * 1.6)));
        animationRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (_) {
      /* Meter is helpful, but not required for permission validation. */
    }
  }, []);

  const checkDevices = useCallback(async () => {
    if (!open) return;
    releaseDevices();
    setStatus("checking");
    setError("");
    setErrorName("");
    setCameraFailed(false);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("MEDIA_UNSUPPORTED");
      }
      let stream;
      let liveAudio = false;
      let liveVideo = false;
      let hasCameraError = false;

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: microphoneIdRef.current
            ? { deviceId: { exact: microphoneIdRef.current } }
            : true,
          video: requirements.video
            ? cameraIdRef.current
              ? { deviceId: { exact: cameraIdRef.current } }
              : true
            : false,
        });
        liveAudio = stream.getAudioTracks().some((track) => track.readyState === "live");
        liveVideo = !requirements.video || stream.getVideoTracks().some((track) => track.readyState === "live");
        if (!liveAudio || (requirements.video && !liveVideo)) {
          stopStream(stream);
          const missing = !liveAudio ? "microphone" : "camera";
          throw new Error(`MISSING_${missing.toUpperCase()}`);
        }
      } catch (err) {
        // If camera failed and we are setting consultant readiness, fall back to audio check
        if (requirements.video && consultantReadiness) {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              audio: microphoneIdRef.current
                ? { deviceId: { exact: microphoneIdRef.current } }
                : true,
              video: false,
            });
            liveAudio = stream.getAudioTracks().some((track) => track.readyState === "live");
            liveVideo = false;
            hasCameraError = true;
            if (!liveAudio) {
              stopStream(stream);
              throw new Error("MISSING_MICROPHONE");
            }
          } catch (audioErr) {
            throw err; // throw original camera error
          }
        } else {
          throw err;
        }
      }

      if (mountedRef.current) {
        setCameraFailed(hasCameraError);
      }

      streamRef.current = stream;
      registerMediaStream(stream);
      if (!hasCameraError && requirements.video && videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => { });
      }
      startMeter(stream);
      setStatus("ready");

      navigator.mediaDevices
        .enumerateDevices()
        .then((listed) => {
          if (!mountedRef.current) return;
          const cameras = listed.filter((device) => device.kind === "videoinput");
          const microphones = listed.filter((device) => device.kind === "audioinput");
          const speakers = listed.filter((device) => device.kind === "audiooutput");
          setDevices({ cameras, microphones, speakers });
          if (!cameraIdRef.current && cameras[0]?.deviceId) {
            cameraIdRef.current = cameras[0].deviceId;
            setCameraId(cameras[0].deviceId);
          }
          if (!microphoneIdRef.current && microphones[0]?.deviceId) {
            microphoneIdRef.current = microphones[0].deviceId;
            setMicrophoneId(microphones[0].deviceId);
          }
          if (!speakerId && speakers[0]?.deviceId) {
            setSpeakerId(speakers[0].deviceId);
          }
        })
        .catch(() => {
          /* Device selection is optional after required tracks are live. */
        });
    } catch (err) {
      console.error("Pre-call device check failed:", err);
      if (mountedRef.current) {
        setStatus("error");
        setErrorName(err?.name || err?.message || "");
        setError(mediaErrorMessage(err, requirements.video));
      }
    }
  }, [open, releaseDevices, requirements.video, startMeter, consultantReadiness]);

  useEffect(() => {
    mountedRef.current = true;
    if (open) checkDevices();
    return () => {
      mountedRef.current = false;
      releaseDevices();
    };
  }, [open, checkDevices, releaseDevices]);

  if (!open) return null;

  const ready = status === "ready";
  const permissionBlocked = ["NotAllowedError", "PermissionDeniedError"].includes(errorName);

  // Custom headers and titles for a professional configuration tool
  const title = consultantReadiness
    ? "Device configuration & setup"
    : normalizedMode === "voice"
      ? "Check your microphone"
      : "Check your camera and microphone";

  const detail = consultantReadiness
    ? "Configure and test your multimedia devices to ensure seamless audio and video calls."
    : normalizedMode === "voice"
      ? "Only microphone permission is requested for this voice consultation."
      : "Camera and microphone are mandatory for this video consultation.";

  // Detect if consultant is already online to show friendlier "Done" button
  let userDetailsObj = null;
  try {
    userDetailsObj = JSON.parse(localStorage.getItem("userDetails") || "{}");
  } catch (_) { }
  const isCurrentlyOnline = userDetailsObj?.onlineStatus === "online";

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center overflow-y-auto bg-slate-950/80 p-3 sm:p-4 backdrop-blur-md">
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl border border-slate-100 transition-all duration-300">

        {/* Header Section */}
        <div className="p-6 pb-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#002C6B]/10 text-[#002C6B]">
              <i className={`fa-solid ${normalizedMode === "voice" ? "fa-microphone" : "fa-desktop"} text-lg`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">{title}</h2>
              <p className="mt-1 text-sm leading-relaxed text-slate-500">{detail}</p>
            </div>
          </div>
        </div>

        {/* Media Preview Section */}
        <div className="p-6 space-y-5">
          {requirements.video ? (
            <div className="relative aspect-video overflow-hidden rounded-2xl bg-slate-900 border border-slate-800 shadow-inner">
              <video ref={videoRef} muted playsInline autoPlay className="h-full w-full object-cover" />
              {(status !== "ready" || cameraFailed) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 text-sm font-medium text-slate-300 bg-slate-950/70 backdrop-blur-sm">
                  {cameraFailed ? (
                    <>
                      <i className="fa-solid fa-video-slash mb-2 text-2xl text-rose-500" />
                      <p className="font-semibold text-slate-200">Camera is unavailable</p>
                      <p className="text-xs text-slate-400 mt-1 max-w-sm">Video calls will be temporarily disabled. You can still proceed with Voice calls and Chat.</p>
                    </>
                  ) : (
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                      {status === "checking" ? "Starting camera preview…" : "Camera preview unavailable"}
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex min-h-[140px] items-center justify-center rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 text-white border border-slate-800 shadow-inner">
              <div className="text-center">
                <i className="fa-solid fa-microphone mb-3 text-3xl text-blue-400 animate-pulse" />
                <p className="text-sm font-semibold tracking-wide text-slate-200">Voice consultation — Camera is inactive</p>
              </div>
            </div>
          )}

          {/* Controls & Inputs */}
          <div className="space-y-4">
            {/* Mic Meter */}
            <div>
              <div className="mb-1.5 flex items-center justify-between text-xs font-semibold text-slate-600">
                <span className="flex items-center gap-1.5">
                  <i className="fa-solid fa-signal text-[10px] text-slate-400" />
                  Microphone Level
                </span>
                <span className={ready ? "text-emerald-600" : "text-amber-500"}>
                  {ready ? "Active" : "Connecting..."}
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-slate-100 border border-slate-200/50 p-0.5">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 transition-[width] duration-75"
                  style={{ width: `${ready ? Math.max(2, micLevel) : 0}%` }}
                />
              </div>
            </div>

            {/* Select Dropdowns */}
            <div className="grid gap-4 sm:grid-cols-2 bg-slate-50/50 p-4 rounded-2xl border border-slate-100">

              {/* Camera Selector */}
              {requirements.video && devices.cameras.length > 0 && (
                <div className="flex flex-col">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <i className="fa-solid fa-video text-[10px]" /> Camera
                  </label>
                  <select
                    value={cameraId}
                    onChange={(event) => {
                      const val = event.target.value;
                      cameraIdRef.current = val;
                      setCameraId(val);
                      checkDevices();
                    }}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                    {devices.cameras.map((device, index) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {friendlyDeviceLabel(device, index, "videoinput")}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Mic Selector */}
              {devices.microphones.length > 0 && (
                <div className="flex flex-col">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <i className="fa-solid fa-microphone text-[10px]" /> Microphone
                  </label>
                  <select
                    value={microphoneId}
                    onChange={(event) => {
                      const val = event.target.value;
                      microphoneIdRef.current = val;
                      setMicrophoneId(val);
                      checkDevices();
                    }}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                    {devices.microphones.map((device, index) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {friendlyDeviceLabel(device, index, "audioinput")}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Speaker Selector */}
              {devices.speakers.length > 0 && (
                <div className="flex flex-col sm:col-span-2">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <i className="fa-solid fa-volume-high text-[10px]" /> Speaker / Audio Output
                  </label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <select
                      value={speakerId}
                      onChange={(event) => setSpeakerId(event.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                      {devices.speakers.map((device, index) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {friendlyDeviceLabel(device, index, "audiooutput")}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={isPlayingSpeakerTest}
                      onClick={async () => {
                        setIsPlayingSpeakerTest(true);
                        const ok = await playSpeakerTest(speakerId);
                        setSpeakerTested(ok);
                        setIsPlayingSpeakerTest(false);
                      }}
                      className={`shrink-0 rounded-xl px-4 py-2.5 text-xs font-bold transition-all shadow-sm flex items-center justify-center gap-2 border ${isPlayingSpeakerTest
                        ? "bg-blue-50 border-blue-200 text-[#002C6B] cursor-wait"
                        : speakerTested
                          ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                          : "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200"
                        }`}>
                      {isPlayingSpeakerTest ? (
                        <>
                          <span className="flex h-2 w-2 relative">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                          </span>
                          Playing...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-circle-play" />
                          {speakerTested ? "Test Sound Played" : "Play Test Sound"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Error Message Box */}
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50/50 p-4 text-sm text-rose-800 shadow-sm">
                <div className="flex gap-2">
                  <i className="fa-solid fa-triangle-exclamation mt-0.5" />
                  <div>
                    <p className="font-bold">
                      {permissionBlocked
                        ? requirements.video
                          ? "Camera and microphone access is restricted"
                          : "Microphone access is restricted"
                        : "Device initialization failed"}
                    </p>
                    <p className="mt-1 opacity-90 text-xs">{error}</p>
                    {permissionBlocked && (
                      <ol className="mt-3 list-decimal space-y-1.5 pl-4 text-xs leading-relaxed opacity-90">
                        <li>Locate the padlock, camera, or settings icon in your browser address bar.</li>
                        <li>
                          Change permissions for {requirements.video ? "Camera and Microphone" : "Microphone"} to <strong>Allow</strong>.
                        </li>
                        <li>Click “Retry permission check” below.</li>
                      </ol>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Section */}
        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
          {onCancel && (
            <button
              type="button"
              onClick={() => {
                releaseDevices();
                onCancel();
              }}
              className="rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 border border-slate-200 shadow-sm hover:bg-slate-50 active:scale-[0.98] transition-all">
              Cancel
            </button>
          )}
          {!ready ? (
            <button
              type="button"
              onClick={checkDevices}
              disabled={status === "checking"}
              className="rounded-xl bg-[#002C6B] px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-[#001f4d] active:scale-[0.98] disabled:cursor-wait disabled:opacity-60 transition-all">
              {status === "checking" ? (
                <span className="flex items-center gap-2">
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-solid border-white border-t-transparent" />
                  Checking...
                </span>
              ) : (
                "Retry permission check"
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                saveDeviceReadiness({ audio: true, video: requirements.video && !cameraFailed });
                releaseDevices();
                onReady?.({
                  mode: normalizedMode,
                  hasMicrophone: true,
                  hasCamera: requirements.video && !cameraFailed,
                });
              }}
              className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-emerald-700 active:scale-[0.98] transition-all">
              {consultantReadiness
                ? isCurrentlyOnline
                  ? "Done & Close"
                  : "Go Online — devices ready"
                : "Join consultation"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
